import { PromiseExtended, Table } from 'dexie';
import {
  apiService,
  crashlyticsService,
  downloadManagerService,
  firebaseAnalyticsService,
  getTenantByName,
  posthogIdentify,
  pushNotificationService,
  SyncChanges,
  SyncRecord,
  SyncRecordStatus,
  toCamelCase,
  userCache,
} from '..';
import { BaseModelForPush } from './base-model';
import { Capacitor } from '@capacitor/core';
import {
  GenericProfile,
  GenericProfileV1,
  IAreaEducationOfficer,
  IPrincipalProfileV1,
  IUserRolesAndPermissions,
  ITeacherProfileV1,
  IUserInformation,
  IUserProfileInvite,
  UserProfile,
  UserToken,
  IAllCoachProfile,
  ICoachProfileV1,
  IAdministrator,
  IRegionalManager,
  ISchools,
  ISchoolGroups,
  IProgramManager,
  IFeatures,
  IProfileLabels,
  ITransferUserProfile,
} from '../types/user';
import { migrations } from '../migrations/user.migration';
import { v4 as uuid4 } from 'uuid';

const TOKEN_RECORD_ID = 10;
const USER_RECORD_ID = 11;
export enum FeatureFlagNames {
  timetableEnabled = 'timetableEnabled',
  intercomEnabled = 'intercomEnabled',
  translationEnabled = 'translationEnabled',
  downloadEnabled = 'downloadEnabled',
  trainingDownloadEnabled = 'trainingDownloadEnabled',
  examGeneratorEnabled = 'examGeneratorEnabled',
  checkpointScreensEnabled = 'checkpointScreensEnabled',
  examCheckerEnabled = 'examCheckerEnabledV2',
  reportCardEnabled = 'reportCardEnabled',
  classRegisterEnabled = 'classRegisterEnabled',
  lpReloadEnabled = 'lpReloadEnabled',
  liveUpdateEnabled = 'liveUpdateEnabled',
  lpDownloadEnabled = 'lpDownloadEnabled',
  inviteParentsEnabled = 'inviteParentsEnabled',
  helpCenterEnabled = 'helpCenterEnabled',
  multiProfileEnabled = 'multiProfileEnabled',
  kainatLpsEnabled = 'kainatLpsEnabled',
  kainatLpSurveyEnabled = 'kainatLpSurveyEnabled',
  trainingEnabled = 'trainingEnabled',
  enabledEditStudentList = 'enabledEditStudentList',
  insightsEnabled = 'insightsEnabled',
  recommendationsEnabled = 'recommendationsEnabled',
  announcementEnabled = 'announcementEnabled',
  flnLpEnabled = 'flnLpEnabled',
  teacherTrainingLevelsEnabled = 'teacherTrainingLevelsEnabled',
  timetableDaysEnabled = 'timetableDaysEnabled',
  lpChapterDownloadEnabled = 'lpChapterDownloadEnabled',
  flnNameDescriptionV2Enabled = 'flnNameDescriptionV2Enabled',
  lpDownloadAbTestEnabled = 'lpDownloadAbTestEnabled',
  lpDownloadWalkthroughEnabled = 'lpDownloadWalkthroughEnabled',
  studentsManagementEnabled = 'studentsManagementEnabled',
  profileManagementEnabled = 'profileManagementEnabled',
  communityEnabled = 'communityEnabled',
  schedulingEnabled = 'schedulingEnabled',
  trainingFeatureEnabled = 'trainingFeatureEnabled',
  coachEnabled = 'coachEnabled',
  fluidLpClassSizeLargeEnabled = 'fluidLpClassSizeLargeEnabled',
  fluidLpClassSizeSmallEnabled = 'fluidLpClassSizeSmallEnabled',
  numeracyEnabled = 'numeracyEnabled',
  profilesScreenUiEnabled = 'profilesScreenUiEnabled',
  diagnosticAssessmentEnabled = 'diagnosticAssessmentEnabled',
  nieteHelpCenterEnabled = 'nieteHelpCenterEnabled',
  scheduleV2Enabled = 'scheduleV2Enabled',
  userGenLpsEnabled = 'userGenLpsEnabled',
  transferProfileEnabled = 'transferProfileEnabled',
  trainingLevelTwoEnabled = 'trainingLevelTwoEnabled',
  registerSchoolEnabled = 'registerSchoolEnabled',
  trainingLevelThreeEnabled = 'trainingLevelThreeEnabled',
  trainingLevelFourEnabled = 'trainingLevelFourEnabled',
  taleemTrackerDashboardEnabled = 'taleemTrackerDashboardEnabled',
  mySchoolEnabled = 'mySchoolEnabled',
  observationFeedbackEnabled = 'observationFeedbackEnabled',
  revisionLpEnabled = 'revisionLpEnabled',
  skillBasedLpEnabled = 'skillBasedLpEnabled',
  prepareLessonPlanEnabled = 'prepareLessonPlanEnabled',
  maxVisibleLevel = 'maxVisibleLevel',
  userGenMediaEnabled = 'userGenMediaEnabled',
  teachingReviewEnabled = 'teachingReviewEnabled',
  ugEgEnabled = 'ugEgEnabled',
  sessionReplayEnabled = 'sessionReplayEnabled',
  externalPartyLpsEnabled = 'externalPartyLpsEnabled',
  beaconhouseFdsEnabled = 'beaconhouseFdsEnabled',
  ugLpAiEditEnabled = 'ugLpAiEditEnabled',
  oxbridgeEnabled = 'oxbridgeEnabled',
  beaconhouseTrainingEnabled = 'beaconhouseTrainingEnabled',
}

const legacyToNewTenantMap = {
  federal: {
    label: 'Federal',
    name: 'fde_production',
    baseUrl: 'https://schools.niete.pk',
    logo: 'https://tb-org-logos.s3.us-east-1.amazonaws.com/federal-tenant.png',
    analytics: 'phc_3UiciaYkIHGt8wjfJzljLnLJvs66sme3XtkdCqHqCAj',
  },
  balochistan: {
    label: 'Balochistan',
    name: 'balochistan_production',
    baseUrl: 'https://balochistan.niete.pk',
    logo: 'https://tb-org-logos.s3.us-east-1.amazonaws.com/balochistan-tenant-logo.png',
    analytics: 'phc_7DWzmE6bw7bVuMGKljpT4ZqiWzeYgRjVvENWqjiaF2y',
  },
  // add others as needed
};
/**
 * This table stores the user and
 * its related profiles and config in this db.
 */
export interface PrincipalProfileRecord
  extends IPrincipalProfileV1, SyncRecord {}
export interface CoachProfileRecord extends ICoachProfileV1, SyncRecord {}
export interface AllCoachProfileRecord extends IAllCoachProfile, SyncRecord {}

export interface TeacherProfileRecord extends ITeacherProfileV1, SyncRecord {}
export interface UserRolesAndPermissionRecord
  extends IUserRolesAndPermissions, SyncRecord {}
export interface UserFeaturesRecord extends IFeatures, SyncRecord {}
export interface UserLabelsRecord extends IProfileLabels, SyncRecord {}
export interface IUserProfileInviteRecord
  extends IUserProfileInvite, SyncRecord {}
export interface AreaEducationOfficerRecord
  extends IAreaEducationOfficer, SyncRecord {}
export interface AdministratorRecord extends IAdministrator, SyncRecord {}
export interface ProgramManagerRecord extends IProgramManager, SyncRecord {}
export interface RegionalManagerRecord extends IRegionalManager, SyncRecord {}
export interface UserInformationRecord extends IUserInformation, SyncRecord {}
export interface TransferUserProfileRecord
  extends ITransferUserProfile, SyncRecord {}

// schools tables
export interface SchoolsRecord extends ISchools, SyncRecord {}
export interface schoolsRegionsRecord extends ISchoolGroups, SyncRecord {}

// Alias for backward compatibility with school-management components
export type SchoolRegistrationRecord = SchoolsRecord;

export class UserDB extends BaseModelForPush {
  token!: Table<UserToken>;
  user!: Table<UserProfile>;
  principalProfilesV2!: Table<PrincipalProfileRecord>;
  teacherProfilesV2!: Table<TeacherProfileRecord>;
  coachProfilesV2!: Table<CoachProfileRecord>;
  allCoachProfilesV2!: Table<AllCoachProfileRecord>;
  areaEducationOfficerProfilesV2!: Table<AreaEducationOfficerRecord>;
  regionalManagerProfilesV2!: Table<RegionalManagerRecord>;
  programManagerProfilesV2!: Table<ProgramManagerRecord>;
  administratorProfilesV2!: Table<AdministratorRecord>;
  userRoles!: Table<UserRolesAndPermissionRecord>;
  usersV1!: Table<UserInformationRecord>;
  schools!: Table<SchoolsRecord>;
  schoolsV1!: Table<SchoolsRecord>;
  schoolRegions!: Table<schoolsRegionsRecord>;
  schoolGroups!: Table<schoolsRegionsRecord>;
  features!: Table<UserFeaturesRecord>;
  profileLabels!: Table<UserLabelsRecord>;
  transferUserProfiles!: Table<TransferUserProfileRecord>;

  constructor() {
    super('userdb');

    migrations.forEach(async ({ version, schema, upgrade }) => {
      const dbVersion = this.version(version).stores(schema);
      if (upgrade) {
        dbVersion.upgrade(upgrade);
      }
    });
    this.setupTableHooks(this.tables);
    this.init();
  }

  async initializeServices() {
    posthogIdentify();
    const userInfo = userCache.getUserFromCache();
    const userId = userInfo?.id;
    if (userId !== undefined && userInfo !== null) {
      crashlyticsService.setUserId(userId);
      firebaseAnalyticsService.setUserProperties(userInfo);
      const pushNotificationToken = await pushNotificationService.getToken();
      if (pushNotificationToken) {
        apiService.post('/users/token/fcm-tokens/', {
          token: pushNotificationToken,
        });
      }
    }
  }

  async init() {
    const profile = await this.getUserProfileFromDB();
    const token = await this.getTokenFromDB();
    this.setRegionsConfigOnLoad();
    if (token) {
      userCache.setTokenToCache(token);
    }
    if (profile) {
      await userCache.setUserToCache(profile);
      this.initializeServices();
    }
  }

  async updateRecord(data: Partial<UserProfile>) {
    return this.user.where('recordId').equals(USER_RECORD_ID).modify(data);
  }

  async setUserProfile(profile: UserProfile) {
    // TODO have a preferred profile option in the future

    const existingProfile = await this.getUserActiveProfile();
    if (!profile.profiles?.length) {
      return;
    }
    for (const p of profile.profiles) {
      if (existingProfile?.id === p.id) {
        p.isSelected = true;

        // Always accept incoming levels from backend.
        // The backend is the source of truth for which profile type (PRIMARY, MIDDLE, HIGH)
        // a user has. Previous logic that preserved existing levels was causing MIDDLE/HIGH
        // teachers to see all training levels because stale ['PRIMARY'] data was never updated.
      } else {
        p.isSelected = false;
      }
    }

    const result = await this.user.put({
      ...profile,
      recordId: USER_RECORD_ID,
    });
    userCache.setUserToCache(profile);
    this.initializeServices();
    return result;
  }

  async setUserActiveProfile(profileId: number): Promise<boolean> {
    const user = await this.getUserProfileFromDB();
    if (user?.profiles) {
      user.profiles.forEach((profile) => {
        profile.isSelected = profile.id === profileId;
      });
      await this.updateRecord(user);
      userCache.setUserToCache(user);
      await this.getActiveRolesPermissions();
      return true;
    }
    return false;
  }

  async getUserActiveProfile(): Promise<GenericProfile | undefined> {
    const user = await this.getUserProfileFromDB();
    if (user?.profiles) {
      return user.profiles.find((profile) => profile.isSelected);
    }
  }

  async getActiveProfileDefaultRoute(): Promise<string | undefined> {
    const activeProfile = await this.getUserActiveProfile();
    const role_id = activeProfile?.role;
    // get the role from the user role table
    const role = await this.userRoles.get(role_id);
    return role?.defaultRoute;
  }

  getUserProfileFromDB(): Promise<UserProfile | undefined> {
    return this.user
      .filter((record) => record.recordId === USER_RECORD_ID)
      .first();
  }

  async setToken(token: UserToken) {
    let result = null;
    const record = await this.token.count();
    if (!record) {
      result = await this.token.add({ ...token, id: TOKEN_RECORD_ID });
    } else {
      result = await this.token.update(TOKEN_RECORD_ID, token);
    }
    userCache.setTokenToCache(token);
    console.log('Setting the user token', result);
    // sync downloads from native platforms when token is set after login
    downloadManagerService.syncDownloads();
    return result;
  }

  getTokenFromDB(): Promise<UserToken | undefined> {
    return this.token.get(TOKEN_RECORD_ID);
  }

  async resetToken() {
    userCache.resetCache();
    return Promise.all([
      this.token.clear(),
      this.user.clear(),
      this.userRoles.clear(),
      this.teacherProfilesV2.clear(),
      this.allCoachProfilesV2.clear(),
      this.coachProfilesV2.clear(),
      this.areaEducationOfficerProfilesV2.clear(),
      this.regionalManagerProfilesV2.clear(),
      this.programManagerProfilesV2.clear(),
      this.administratorProfilesV2.clear(),
      this.schools.clear(),
      this.schoolsV1.clear(),
      this.schoolRegions.clear(),
      this.schoolGroups.clear(),
      this.principalProfilesV2.clear(),
      this.usersV1.clear(),
    ]);
  }

  async getFeatureFlag(name: string) {
    const data = await this.user
      .filter((record) => record.featureFlags != null)
      .toArray();
    return data[0]?.featureFlags[name];
  }
  async getActiveRolesPermissions() {
    const activeProfile = await this.getUserActiveProfile();
    const data = await this.userRoles
      .filter((record) => record.id === activeProfile?.role) // till new version of profile endpoint is not implemented
      .toArray();
    userCache.setActiveRolePermission(data[0]);
    return data[0];
  }
  async getAllRolesPermissions() {
    const data = await this.userRoles.toArray();
    return data;
  }
  async getAllFeatureFlags() {
    const data = await this.user
      .filter((record) => record.featureFlags != null)
      .toArray();
    return data[0]?.featureFlags;
  }
  async setRegionsConfigOnLoad() {
    if (process.env.NX_PUBLIC_ENV === 'local') {
      return;
    }

    // for legacy tenant
    if (Capacitor.isNativePlatform()) {
      const region = localStorage.getItem('selectedRegion');
      if (region) {
        const regionKey =
          region.toLowerCase() as keyof typeof legacyToNewTenantMap;
        const data = legacyToNewTenantMap[regionKey];
        if (!data) {
          userCache.setRegionToCache(JSON.parse(region));
        }
        if (data) {
          localStorage.setItem('selectedRegion', JSON.stringify(data));
          userCache.setRegionToCache(data);
        }
      }
    }
    // for web
    const regionSelected = await getTenantByName(window.origin);
    if (regionSelected && !Capacitor.isNativePlatform()) {
      localStorage.setItem('selectedRegion', JSON.stringify(regionSelected));
      userCache.setRegionToCache(regionSelected);
    }

    posthogIdentify();
  }

  // for users profile.

  async addUserInformation(phoneNumber: string, uuid: string, name: string) {
    await this.usersV1.put({
      username: phoneNumber,
      uuid: uuid,
      name: name,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    });
  }

  async getExistingUser(phoneNumber: string) {
    if (typeof phoneNumber !== 'string' || phoneNumber.length === 0) {
      return null;
    }
    const normalizedPhone = phoneNumber?.startsWith('0')
      ? phoneNumber
      : '0' + phoneNumber;
    const data = await this.usersV1.get({ username: normalizedPhone });
    return data;
  }
  // for all profiles models as data for the each model can vary.
  async getProfileExists(username: string, model: any) {
    const normalizedPhone = username.startsWith('0')
      ? username
      : `0${username}`;
    // const currentUser = await this.usersV1.get({ username: normalizedPhone });
    // if user is not found in users table then

    // @ts-expect-error
    const profileExist = await userDB[model].get({
      userUsername: normalizedPhone,
    });

    return profileExist ? true : false;
  }

  async getProfileLabels(): Promise<IProfileLabels[]> {
    const labels = await this.profileLabels
      .filter((data) => data.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
    return labels;
  }

  async getProfileFeatures(): Promise<IFeatures[]> {
    const features = await this.features
      .filter((data) => data.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
    return features;
  }

  async deleteUserProfile(profile: GenericProfileV1, model: string) {
    const selectedProfile =
      (await this.getUserActiveProfile()) as GenericProfile;
    // @ts-expect-error

    const profileExist = await this[model]
      .where('uuid')
      .equals(profile.uuid)
      .first();

    // Rows that never reached the server (CreatedButNotSynced) and rows the
    // server permanently rejected (Failed, e.g. MC20-18296 orphaned profiles
    // whose server-side id no longer exists) should both be hard-deleted
    // locally. Flipping them back to syncStatus='deleted' would re-enqueue
    // the same broken DELETE on the next push.
    const shouldHardDelete =
      profileExist?.syncStatus === SyncRecordStatus.CreatedButNotSynced ||
      profileExist?.syncStatus === SyncRecordStatus.Failed;

    if (shouldHardDelete) {
      // @ts-expect-error
      await this[model].delete(profile.uuid);
    } else {
      // Mark the profile as deleted
      // @ts-expect-error
      await this[model].where({ uuid: profile.uuid }).modify({
        syncStatus: SyncRecordStatus.Deleted,
        sentByProfileModel: `${toCamelCase(selectedProfile.type).toLowerCase()}profile`,
        sentByProfileId: selectedProfile.id,
      });
    }
  }

  async addUserProfile(model: string, data: any) {
    // check if user already exists
    const uuid = uuid4();
    const phoneNumber = data.username.startsWith('0')
      ? data.username
      : `0${data.username}`;
    const existingUser = await userDB.getExistingUser(phoneNumber);

    // Remove username from data and use userUsername instead
    const { username, ...dataWithoutUsername } = data;
    const profileData = {
      ...dataWithoutUsername,
      userUsername: phoneNumber,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    };

    if (!existingUser) {
      await this.addUserInformation(phoneNumber, uuid, data.name);
      this.addProfileToUser(profileData, model, uuid);
    } else {
      this.addProfileToUser(profileData, model, uuid);
    }
  }

  async addProfileToUser(data: any, model: string, uuid: string) {
    data.uuid = uuid;
    // @ts-expect-error
    userDB[model].add(data);
  }

  async createTransferProfile(data: ITransferUserProfile) {
    // Check if table exists
    if (!this.transferUserProfiles) {
      throw new Error('transferUserProfiles table is not initialized');
    }

    const transferData = {
      ...data,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    };
    const result = await this.transferUserProfiles.add(transferData);
    return result;
  }

  async getFullProfileByUuid(
    tableName: string,
    profileUuid: string,
  ): Promise<any> {
    const table = (this as any)[tableName];
    if (!table) {
      throw new Error(`Table ${tableName} not found`);
    }

    const profile = await table.where('uuid').equals(profileUuid).first();
    return profile;
  }

  async getSchools(): Promise<SchoolsRecord[]> {
    return this.schoolsV1.toArray();
  }
  async getSchoolById(id: number): Promise<SchoolsRecord | null> {
    // Query by 'id' field (not primary key 'uuid')
    const result = await this.schoolsV1.where('id').equals(id).first();
    return result ?? null;
  }
  async getSchoolRegions(): Promise<schoolsRegionsRecord[]> {
    return this.schoolRegions.toArray();
  }
  async getSchoolGroups(): Promise<schoolsRegionsRecord[]> {
    return this.schoolGroups.toArray();
  }
  private async getProfileToBeSynced(
    model: Table,
  ): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await model
      .where('syncStatus')
      .anyOf([
        SyncRecordStatus.CreatedButNotSynced,
        SyncRecordStatus.Deleted,
        SyncRecordStatus.Updated,
      ])
      .toArray();

    return records.map((record) => {
      // Profile-table rows carry their own actor identity in `type`/`id` once
      // they exist on the server.
      //
      // Updated rows: self-stamp from record.type/record.id. A profile switch
      //   mid-flush must not mis-stamp the actor (MC20-18283 — backend would
      //   reject with "Profile X does not have permission to 'update'").
      // Deleted rows: actor was already captured at click time by
      //   deleteUserProfile() and written to the Dexie row. Read it back here
      //   rather than recomputing from record.type/record.id (which would stamp
      //   the *target* of the delete as the *actor* — MC20-18337).
      //   Falls back to active-profile if the row was written before this fix.
      // Created rows: no server id, no captured actor — use the active profile
      //   (whoever is creating the record).
      // Non-profile rows (usersV1, schoolsV1): no `type` — active-profile read.
      const recordType: string | undefined = record?.type;
      const isUpdate = record.syncStatus === SyncRecordStatus.Updated;
      const isDeleted = record.syncStatus === SyncRecordStatus.Deleted;
      const hasStoredActor =
        record.sentByProfileId != null &&
        typeof record.sentByProfileModel === 'string' &&
        record.sentByProfileModel.length > 0;
      const useSelfStamp =
        isUpdate &&
        typeof recordType === 'string' &&
        recordType.length > 0 &&
        record.id != null;
      const useStoredActor = isDeleted && hasStoredActor;

      let sentByProfileId: number | undefined;
      if (useSelfStamp) {
        sentByProfileId = record.id;
      } else if (useStoredActor) {
        sentByProfileId = record.sentByProfileId;
      } else {
        sentByProfileId = userCache.getActiveProfile()?.id;
      }

      let sentByProfileModel: string;
      if (useSelfStamp) {
        sentByProfileModel = `${toCamelCase(recordType).toLowerCase()}profile`;
      } else if (useStoredActor) {
        sentByProfileModel = record.sentByProfileModel;
      } else {
        sentByProfileModel = `${toCamelCase(userCache.getActiveProfile()?.type).toLowerCase()}profile`;
      }

      return {
        ...record,
        sentByProfileId,
        sentByProfileModel,
        syncStatus:
          record.syncStatus === SyncRecordStatus.CreatedButNotSynced
            ? SyncRecordStatus.Created
            : record.syncStatus,
      };
    });
  }

  async getUsersProfileInformation(): Promise<GenericProfileV1[]> {
    const users = await this.usersV1.toArray();
    const userMap = new Map(users.map((u) => [u.username, u]));
    const userFeatures = await this.getProfileFeatures();

    const profileTable = [
      'principalProfilesV2',
      'teacherProfilesV2',
      'coachProfilesV2',
      'areaEducationOfficerProfilesV2',
      'regionalManagerProfilesV2',
      'programManagerProfilesV2',
      'administratorProfilesV2',
    ] as const;

    type ProfileTableName = (typeof profileTable)[number];

    const activeUsers = await this.getActiveRolesPermissions();
    const managableProfiles = activeUsers.accessControls
      .find((data) =>
        userFeatures.some(
          (feature) =>
            feature.id === data.feature && feature.name === 'user_management',
        ),
      )
      ?.canManageProfiles.filter(
        (data) =>
          !toCamelCase(data).includes(
            toCamelCase(userCache.getActiveProfile().type),
          ),
      ); //todo: refactor, filter out the current profile type temp fix
    const profileTables: ProfileTableName[] = (
      managableProfiles?.map((profile) => {
        const tableName = `${toCamelCase(profile)}sV2` as string;
        if (profileTable.includes(tableName as ProfileTableName)) {
          return tableName as ProfileTableName;
        }
        return null;
      }) ?? []
    ).filter(Boolean) as ProfileTableName[];

    const enrichedProfiles: GenericProfileV1[] = [];

    for (const tableName of profileTables) {
      const profiles = (await userDB[tableName].toArray()).filter(
        (data) => data.syncStatus !== SyncRecordStatus.Deleted,
      );
      profiles.forEach((profile) => {
        const user = userMap.get(profile.userUsername);
        enrichedProfiles.push({
          ...profile,
          name: user?.name ?? '',
          model: tableName,
          username: user?.username ?? '',
        });
      });
    }

    return enrichedProfiles;
  }

  // to update the profile information
  async getProfileByUuid(uuid: string) {
    const userProfiles = await this.getUsersProfileInformation();

    return userProfiles.find((profile) => profile.uuid === uuid);
  }

  async getUpdateProfile(profile: GenericProfileV1 & SyncRecord) {
    const tableName = `${toCamelCase(profile.type)}ProfilesV2`;

    // Preserve CreatedButNotSynced (fresh local create still pending its first
    // push) so the edit still posts as a create, not an update. Otherwise stamp
    // Updated — without this, edits to a Synced profile stay invisible to the
    // sync flush (getProfileToBeSynced only picks up Created/Updated/Deleted).
    const recordToPut = {
      ...profile,
      syncStatus:
        profile.syncStatus === SyncRecordStatus.CreatedButNotSynced
          ? SyncRecordStatus.CreatedButNotSynced
          : SyncRecordStatus.Updated,
    };

    try {
      // @ts-expect-error
      const result = await this[tableName].put(recordToPut);
      return result;
    } catch (error) {
      console.error('Error updating profile in IndexedDB:', {
        tableName,
        error,
        profile,
      });
      throw error;
    }
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      user: await this.getProfileToBeSynced(this.usersV1),
      teacherProfile: await this.getProfileToBeSynced(this.teacherProfilesV2),
      principalProfile: await this.getProfileToBeSynced(
        this.principalProfilesV2,
      ),
      coachProfile: await this.getProfileToBeSynced(this.coachProfilesV2),
      allCoachProfile: await this.getProfileToBeSynced(this.allCoachProfilesV2),
      areaEducationOfficerProfile: await this.getProfileToBeSynced(
        this.areaEducationOfficerProfilesV2,
      ),
      regionalManagerProfile: await this.getProfileToBeSynced(
        this.regionalManagerProfilesV2,
      ),
      programManagerProfile: await this.getProfileToBeSynced(
        this.programManagerProfilesV2,
      ),
      administratorProfile: await this.getProfileToBeSynced(
        this.administratorProfilesV2,
      ),
      transferUserProfiles: await this.getProfileToBeSynced(
        this.transferUserProfiles,
      ),
      school: await this.getProfileToBeSynced(this.schoolsV1),
    };

    return records;
  }
  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;

    // Mapping of profile types to table names and ID fields
    const profileMappings = [
      { key: 'user', table: 'usersV1', idField: 'username' },
      { key: 'teacherProfile', table: 'teacherProfilesV2', idField: 'uuid' },
      {
        key: 'principalProfile',
        table: 'principalProfilesV2',
        idField: 'uuid',
      },
      { key: 'coachProfile', table: 'coachProfilesV2', idField: 'uuid' },
      { key: 'allCoachProfile', table: 'allCoachProfilesV2', idField: 'uuid' },
      {
        key: 'areaEducationOfficerProfile',
        table: 'areaEducationOfficerProfilesV2',
        idField: 'uuid',
      },
      {
        key: 'regionalManagerProfile',
        table: 'regionalManagerProfilesV2',
        idField: 'uuid',
      },
      {
        key: 'programManagerProfile',
        table: 'programManagerProfilesV2',
        idField: 'uuid',
      },
      {
        key: 'administratorProfile',
        table: 'administratorProfilesV2',
        idField: 'uuid',
      },
      {
        key: 'transferUserProfiles',
        table: 'transferUserProfiles',
        idField: 'uuid',
      },
      {
        key: 'school',
        table: 'schoolsV1',
        idField: 'uuid',
      },
    ];

    // Process each profile type
    for (const { key, table, idField } of profileMappings) {
      const profileChanges = syncChanges[key as keyof SyncChanges];
      if (!profileChanges) continue;

      // Created/updated records get stamped with the new status (Synced or Failed).
      const upsertIds = [
        ...(profileChanges.created?.map((record: any) => record[idField]) ||
          []),
        ...(profileChanges.updated?.map((record: any) => record[idField]) ||
          []),
      ].filter(Boolean);

      if (upsertIds.length > 0) {
        updates += await this._updateSyncData(
          // @ts-ignore
          this[table],
          upsertIds,
          status,
        );
      }

      // Deleted records: on success remove from Dexie entirely (the server
      // accepted the DELETE — no need to keep a tombstone). On failure stamp
      // Failed so MC20-18296's retry guard can surface it and the next
      // cancel-click can hard-delete via deleteUserProfile.
      const deletedIds = (
        profileChanges.deleted?.map((record: any) => record[idField]) || []
      ).filter(Boolean);

      if (deletedIds.length > 0) {
        if (status === SyncRecordStatus.Synced) {
          const primaryKey =
            // @ts-ignore
            (this[table] as Table).schema.primKey.keyPath ?? idField;
          // @ts-ignore
          updates += await (this[table] as Table)
            .where(primaryKey)
            .anyOf(deletedIds)
            .delete();
        } else {
          updates += await this._updateSyncData(
            // @ts-ignore
            this[table],
            deletedIds,
            status,
          );
        }
      }
    }

    return updates;
  }

  // School-related methods
  async getSchoolsBySchoolGroup(schoolGroupId: number) {
    return this.schoolsV1.where('group').equals(schoolGroupId).toArray();
  }

  // School Registration methods (using schoolsV1)
  async addSchoolRegistration(schoolData: Partial<ISchools>): Promise<string> {
    // Validate required fields
    if (
      !schoolData.emis ||
      !schoolData.group ||
      schoolData.numberOfStudents === undefined ||
      schoolData.numberOfTeachers === undefined
    ) {
      throw new Error(
        'Missing required fields: emis, group, numberOfStudents, and numberOfTeachers are required',
      );
    }

    const uuid = uuid4();
    const recordData: SchoolsRecord = {
      name: schoolData.name || '',
      address: schoolData.address || '',
      city: schoolData.city || '',
      emis: schoolData.emis,
      region: schoolData.region ?? null,
      group: schoolData.group,
      numberOfStudents: schoolData.numberOfStudents,
      numberOfTeachers: schoolData.numberOfTeachers,
      id: schoolData.id,
      profileType: schoolData.profileType,
      uuid,
      syncStatus: SyncRecordStatus.CreatedButNotSynced,
    };
    await this.schoolsV1.add(recordData);
    return uuid;
  }

  async updateSchoolRegistration(
    uuid: string,
    changes: Partial<ISchools>,
  ): Promise<number> {
    const existing = await this.schoolsV1.get(uuid);
    if (!existing) return 0;

    const updateData: Partial<SchoolsRecord> = {
      ...changes,
    };

    if (existing.syncStatus !== SyncRecordStatus.CreatedButNotSynced) {
      updateData.syncStatus = SyncRecordStatus.Updated;
    }

    return this.schoolsV1.update(uuid, updateData);
  }

  async deleteSchoolRegistration(uuid: string): Promise<number> {
    return this.schoolsV1.update(uuid, {
      syncStatus: SyncRecordStatus.Deleted,
    });
  }

  async getSchoolRegistrationByUuid(
    uuid: string,
  ): Promise<SchoolsRecord | undefined> {
    return this.schoolsV1.get(uuid);
  }

  async getAllSchoolRegistrations(): Promise<SchoolsRecord[]> {
    return this.schoolsV1
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  async getFirstRegionalManagerProfileSafe() {
    if (!this.regionalManagerProfilesV2) {
      return undefined;
    }
    try {
      return await this.regionalManagerProfilesV2.toCollection().first();
    } catch (error) {
      return undefined;
    }
  }

  async getAllCoachProfilesSafe() {
    if (!this.allCoachProfilesV2) {
      return [];
    }
    try {
      return await this.allCoachProfilesV2
        .filter((data) => data.syncStatus !== SyncRecordStatus.Deleted)
        .toArray();
    } catch (error) {
      return [];
    }
  }

  async getCurrentUserProfiles(): Promise<any> {
    const user = await this.getUserProfileFromDB();
    // Read selected profile from DB instead of cache to ensure useLiveQuery reactivity
    const selectedProfile = user?.profiles?.find((p) => p.isSelected);
    const labels = await this.getProfileLabels();

    // Guard: Check if user exists and has username
    if (!user?.username) {
      return {
        name: '',
        additionalContact: 'N/A',
        type: '',
      };
    }

    const userInfo = await this.usersV1
      .where('username')
      .equals(user.username)
      .first();

    // Guard: Check if selectedProfile exists and has type
    if (!selectedProfile || !selectedProfile.type) {
      return {
        name: userInfo?.name ?? '',
        additionalContact: userInfo?.additionalPhoneNumbers?.[0] ?? 'N/A',
        type: '',
      };
    }

    // @ts-ignore
    // Query by userUsername and filter by profile id to get the correct profile
    // when a user has multiple profiles of the same type at different schools
    const profilesForUser = await this[
      `${toCamelCase(selectedProfile.type)}ProfilesV2`
    ]
      .where('userUsername')
      .equals(user.username)
      .toArray();

    // Find the specific profile that matches the selected profile's id
    const selectedProfileData =
      profilesForUser.find(
        (profile: any) => profile.id === selectedProfile.id,
      ) || profilesForUser[0];
    return {
      name: userInfo?.name ?? '',
      ...selectedProfileData,
      additionalContact: userInfo?.additionalPhoneNumbers?.[0] ?? 'N/A',
      type:
        labels.find(
          (label) =>
            label.profileType ===
            `${selectedProfile.type.toLowerCase()}_profile`,
        )?.label ?? selectedProfile.type,
    };
  }
}

export const userDB = new UserDB();
